"use client";

import * as React from "react";
import { useRef, useState, useLayoutEffect } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";

const AnimatedTabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ activationMode = "manual", ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} activationMode={activationMode} {...props} />
));
AnimatedTabs.displayName = "AnimatedTabs";

const AnimatedTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    value?: string;
    animateInitial?: boolean;
  }
>(({ className, value, animateInitial = true, children, ...props }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const initialValue = useRef(value);
  const [hasChangedTab, setHasChangedTab] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    if (value !== initialValue.current) setHasChangedTab(true);
    const container = containerRef.current;
    if (!container) return;

    const updateIndicator = () => {
      const activeButton = container.querySelector<HTMLButtonElement>(
        '[role="tab"][data-state="active"]',
      );
      if (!activeButton) return;

      const nextStyle = {
        left: activeButton.offsetLeft,
        width: activeButton.offsetWidth,
      };
      setIndicatorStyle((previous) =>
        previous.left === nextStyle.left && previous.width === nextStyle.width
          ? previous
          : nextStyle,
      );
    };

    updateIndicator();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateIndicator);
    observer.observe(container);
    // Sibling widths can move the active tab without changing the list width.
    container.querySelectorAll('[role="tab"]').forEach((tab) => {
      observer.observe(tab);
    });

    return () => observer.disconnect();
  }, [value, children]);

  return (
    <TabsPrimitive.List
      ref={ref}
      tabIndex={-1}
      className={cn(
        "relative inline-flex h-9 w-full items-center justify-center gap-1 rounded-sm bg-card p-1 text-secondary-foreground",
        className,
      )}
      {...props}
    >
      <div
        ref={containerRef}
        className="relative flex h-full w-full items-center"
      >
        {/* Sliding indicator */}
        <div
          className={cn(
            "absolute h-[calc(100%-2px)] rounded-sm bg-background shadow",
            (animateInitial || hasChangedTab) &&
              "transition-all duration-200 ease-out",
          )}
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />
        {children}
      </div>
    </TabsPrimitive.List>
  );
});
AnimatedTabsList.displayName = "AnimatedTabsList";

const AnimatedTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, tabIndex, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "relative z-10 inline-flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-sm px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand-primary disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-primary-foreground data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground",
      className,
    )}
    {...props}
    tabIndex={tabIndex ?? 0}
  />
));
AnimatedTabsTrigger.displayName = "AnimatedTabsTrigger";

const AnimatedTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    tabIndex={-1}
    className={cn(
      "mt-2 focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand-primary",
      className,
    )}
    {...props}
  />
));
AnimatedTabsContent.displayName = "AnimatedTabsContent";

export {
  AnimatedTabs,
  AnimatedTabsList,
  AnimatedTabsTrigger,
  AnimatedTabsContent,
};
